import { getQuestionsQuery, getArgumentsFromStatements, getGroupedArgumentsForGradationQuestion } from "../firebaseWrapper/firebaseWrapper";
import { convertInfoToPath, convertRouteParamsToInfo } from "../helperFunctions";
import { strings } from "../localization/localization.web";
import { ContainerType, ElementPath, FirestoreConstants, PathConstants, StatementRole, elementId } from "../typings/types";
import { LazyList } from "./FeedList";
import { ArgumentThreadHeader, LineToSideOfViewMobile } from "./StatementScreen";
import { useFocusEffect } from '@react-navigation/native';
import React, { useEffect, useState } from 'react';
import DraggableFlatList, {
  ScaleDecorator,
} from 'react-native-draggable-flatlist';
import { Layout } from 'react-native-reanimated';
import { ActionSheet, Colors, Text, View } from 'react-native-ui-lib';
import {
  ArgumentData,
  useQuestionListener,
} from '../dataModel';
import {
  convertPathToInfo,
  isEditingAllowed,
} from '../helperFunctions';
import { QuestionAnswerType, StanceType } from '../typings/types';
import { ArgumentCard, QuestionListItem } from './Containers';
import { useIsDesktop, useIsMobile, useIsTablet } from '../MediaQueries';
import { Entypo } from '@expo/vector-icons';
import { doc, DocumentData, getDoc, getFirestore } from 'firebase/firestore';
import { useUserState } from '../firebaseWrapper/firebaseWrapper';
import { StatementView, StatementViewProps } from "./StatementView";
import { Pressable } from "react-native";

type QuestionViewListProps = {
  navigation: any;
  route: any;
  location: PathConstants;
  groupId: any;
  topicId: any;
  memberId: any;
  groupAdminLoggedIn: any;
};

export function QuestionViewList({
  navigation,
  route,
  location,
  groupId,
  topicId,
  memberId,
  groupAdminLoggedIn,
}: QuestionViewListProps): JSX.Element {


  return (
    <LazyList
      navigation={navigation}
      route={route}
      itemType={ContainerType.QUESTION}
      query={getQuestionsQuery(topicId, groupId, undefined)}
      location={location}
      groupId={groupId}
      memberId={memberId}
      groupAdminLoggedIn={groupAdminLoggedIn}
      header={strings.main_questions}
      showStatementView={true}
    />
  )
}

export function QuestionView({
  navigation,
  route,
  location,
  statementId,
  memberId,
  groupId,
  groupAdminLoggedIn,
  role
}: StatementViewProps): JSX.Element {
  const isMobile = useIsMobile();
  const isTablet = useIsTablet();
  const isDesktop = useIsDesktop();
  const routeMemberId = route?.params?.member;
  const routeGroupId = route?.params?.groupId;
  const [group, setGroup] = React.useState<DocumentData>();
  const [adminLoggedIn, setAdminLoggedIn] = React.useState(false);
  const [user] = useUserState();
  const [argumentsCollapsed, setArgumentsCollapsed] = useState(false);
  const type = ContainerType.QUESTION;
  const info: ElementPath = convertRouteParamsToInfo(route?.params, location, statementId, type, groupId) as ElementPath;
  const { question, loading, error } = useQuestionListener(info);
  const [activeListType, setActiveListType] = useState('answers');
  const [defaultListTypeApplied, setDefaultListTypeApplied] = useState(false);
  const listTypeOptions = ['answers', 'arguments'];
  const header = undefined;
  const [showDropdownOptions, setShowDropdownOptions] = useState(false);

  // Picks the initial list type from the question's answer type (gradation -> grouped answers,
  // alternative -> single arguments) the first time it loads, without overriding a manual
  // switch the viewer makes afterwards (question re-renders on every Firestore update).
  useEffect(() => {
    if (!loading && !defaultListTypeApplied) {
      setActiveListType(question.getAnswerType() === QuestionAnswerType.ALTERNATIVE ? 'arguments' : 'answers');
      setDefaultListTypeApplied(true);
    }
  }, [loading]);

  useEffect(() => {
    if (groupId !== undefined) {
      const documentReference = doc(getFirestore(), FirestoreConstants.GROUPS, groupId);
      getDoc(documentReference).then((documentSnapshot) => {
        if (documentSnapshot.exists()) {
          setGroup({ ...documentSnapshot.data(), id: documentSnapshot.id });
        } else {
          console.log('No such document!');
        }
      }).catch((error) => {
        console.log('Error getting document:', error);
      });
    }
  }, []);

  useEffect(() => {
    if (group && user) {
      setAdminLoggedIn(group.admin === user.uid);
    }
  }, [group, user]);

  const [argumentsData, setArguments] = React.useState<({ [path: string]: StanceType } | ArgumentData)[]>([
    { [convertInfoToPath(info)]: StanceType.NEUTRAL },
  ]);
  // For a gradation Question, each argument's conclusion stance relative to the Question's
  // underlying claim (see getGroupedArgumentsForGradationQuestion), keyed by argument path.
  // Drives the thread-line color in the flat "arguments" list so arguments that share the same
  // or an opposing conclusion read as merged/grouped rather than flat and neutral. Empty (and
  // every argument defaults to NEUTRAL) for alternative questions, which keep today's behavior.
  const [argumentConclusionStances, setArgumentConclusionStances] = React.useState<{ [path: string]: StanceType }>({});
  const [answerStatements, setAnswerStatements] = React.useState([
    { [convertInfoToPath(info)]: StanceType.NEUTRAL },
  ]);
  const [transitionFinished, setTransitionFinished] = useState(false);

  useFocusEffect(() => {
    if (!transitionFinished) {
      setTransitionFinished(true);
    }
  });

  useEffect(() => {
    if (question && transitionFinished) {
      if (activeListType === 'answers') {
        setAnswerStatements([{ [convertInfoToPath(info)]: StanceType.NEUTRAL }, ...question.getChildData()]);
      } else if (activeListType === 'arguments') {
        const answerIds: elementId[] = question.getChildInfos().map((answerInfo) => answerInfo.id);
        if (question.getAnswerType() === QuestionAnswerType.GRADATION) {
          // Gradation answers are degrees of the same claim, so arguments across different
          // answers that share the same or an opposing conclusion are merged into one combined
          // view: grouped together (same conclusion adjacent) instead of a flat neutral list.
          getGroupedArgumentsForGradationQuestion(question, info.loc, groupId).then(
            (grouped) => {
              setArguments([
                { [convertInfoToPath(info)]: StanceType.NEUTRAL },
                ...grouped.map(({ argument }) => argument),
              ]);
              setArgumentConclusionStances(
                grouped.reduce((stances, { argument, stance }) => {
                  stances[argument.getPath()] = stance;
                  return stances;
                }, {} as { [path: string]: StanceType }),
              );
            }
          )
        } else {
          // Alternative answers are genuinely distinct options -- arguments stay flat and
          // neutral even when their conclusions happen to coincide.
          getArgumentsFromStatements(answerIds, info.loc, groupId).then(
            (data) => {
              setArguments([{ [convertInfoToPath(info)]: StanceType.NEUTRAL }, ...data]);
              setArgumentConclusionStances({});
            }
          )
        }
      }
    }
  }, [question, transitionFinished, activeListType]);

  if (error) {
    return (
      <View flex center>
        <Text>Error: {error.message}</Text>
      </View>
    );
  }

  const onDragEnd = ({ data }) => {
    if (!isEditingAllowed(question)) {
      return;
    }
    question.setChildData(
      data.filter(item => Object.keys(item)[0] !== convertInfoToPath(info)),
    );
    setArguments([
      { [convertInfoToPath(info)]: StanceType.NEUTRAL },
      ...data.filter(item => Object.keys(item)[0] !== convertInfoToPath(info)),
    ]);
  };

  const renderArgument = ({ item, getIndex, drag, isActive }) => {
    if (getIndex() === 0) {
      return <>
        {argumentsData.length > 1 ? <ArgumentThreadHeader stance={StanceType.NEUTRAL} /> : null}
      </>;
    } else {
      // Real rows (index > 0) are always ArgumentData: getArgumentsFromStatements returns a flat
      // list of arguments across multiple answers, so there's no single stance relative to the
      // question itself the way there is for a direct child. For gradation questions, the thread
      // line instead reflects the argument's computed conclusion stance (see
      // getGroupedArgumentsForGradationQuestion), grouping same/opposing conclusions together;
      // for alternative questions argumentConclusionStances is empty and this stays NEUTRAL.
      const argument = item as ArgumentData;
      const stance = argumentConclusionStances[argument.getPath()] ?? StanceType.NEUTRAL;
      return (
        <LineToSideOfViewMobile
          key={argument.getPath()}
          stance={stance}
          lastLine={getIndex() === argumentsData.length - 1 && !isActive}
          isActive={isActive}
        >
          <ScaleDecorator>
            <View marginH-0 marginL-0>
              <ArgumentCard
                navigation={navigation}
                info={argument.getInfo()}
                argument={argument}
              />
            </View>
          </ScaleDecorator>
        </LineToSideOfViewMobile>
      );
    }
  };

  const renderAnswer = ({ item, getIndex, drag, isActive }) => {
    if (getIndex() === 0) {
      return <>
        {answerStatements.length > 1 ? <ArgumentThreadHeader stance={StanceType.NEUTRAL} /> : null}
      </>;
    } else {
      return (
        <LineToSideOfViewMobile
          key={Object.keys(item)[0]}
          stance={item[Object.keys(item)[0]]}
          lastLine={getIndex() === answerStatements.length - 1 && !isActive}
          isActive={isActive}
        >
          <ScaleDecorator>
            <View marginH-0 marginL-0>
              <StatementView
                navigation={navigation}
                location={{ ...info, ...convertPathToInfo(Object.keys(item)[0]) }.loc}
                statementId={{ ...info, ...convertPathToInfo(Object.keys(item)[0]) }.id}
                groupId={groupId}
                groupAdminLoggedIn={groupAdminLoggedIn}
                memberId={memberId}
                argumentsCollapsed={argumentsCollapsed}
              />
            </View>
          </ScaleDecorator>
        </LineToSideOfViewMobile>
      );
    }
  };

  return (
    <>
      {isMobile || isTablet || isDesktop ?
        <View style={{
          backgroundColor: Colors.secondaryBG,
          borderRadius: 15,
        }}>
          <View margin-0 style={{ flexDirection: 'column-reverse' }}>
            <QuestionListItem
              info={info}
              parentInfo={null}
              navigation={navigation}
              drag={undefined}
              showStanceIndicator={false}
              showHeader={role !== StatementRole.PREMISE}
              showDetails={role !== StatementRole.PREMISE}
              groupId={groupId}
              memberId={memberId}
              groupAdminLoggedIn={groupAdminLoggedIn}
              handleArgumentsPress={setArgumentsCollapsed ? () => setArgumentsCollapsed(!argumentsCollapsed) : undefined}
              role={role}
            />
          </View>
        </View>
        : null
      }
      {argumentsData.length > 0 || answerStatements.length > 0 && !argumentsCollapsed ?
        <DraggableFlatList
          showsVerticalScrollIndicator={false}
          dragItemOverflow={true}
          keyboardShouldPersistTaps="handled"
          data={activeListType === 'answers' ? answerStatements : argumentsData}
          onDragEnd={onDragEnd}
          containerStyle={{ flex: 1 }}
          itemLayoutAnimation={Layout}
          renderPlaceholder={() => (
            <LineToSideOfViewMobile stance={StanceType.NEUTRAL} lastLine={false} />
          )}
          refreshing={loading}
          keyExtractor={item => 'getPath' in item ? item.getPath() : Object.keys(item)[0]}
          stickyHeaderIndices={[0]}
          stickyHeaderHiddenOnScroll={true}
          renderItem={activeListType === 'answers' ? renderAnswer : renderArgument}
          ListHeaderComponent={true || header ?
            <View row style={{ justifyContent: header ? 'space-between' : 'flex-end', marginRight: 10, marginLeft: 5 + 30, marginBottom: header ? 10 : -30 }}>
              {header ?
                <Text textPrimary h2>
                  {header}
                </Text>
                : null
              }
              <Pressable
                testID="list-type-toggle"
                onPress={() => setShowDropdownOptions(true)}>
                <View row style={{ alignItems: 'center', justifyContent: 'flex-end' }}>
                  <Text p textSecondary style={{}}>
                    {activeListType === 'answers' ? strings.argumentListType.grouped : strings.argumentListType.single}
                  </Text>
                  <Entypo name="chevron-small-down" size={24} color={Colors.textSecondary} />
                </View>
              </Pressable>
            </View>
            : null
          }
        />
        : null
      }
      <ActionSheet
          visible={showDropdownOptions}
          dialogStyle={{
            borderTopLeftRadius: 30,
            borderTopRightRadius: 30,
            backgroundColor: Colors.quaternaryBG,
          }}
          options={[
            {
              label: strings.argumentListType.grouped,
              testID: 'list-type-answers',
              onPress: () => {
                setActiveListType("answers");
              },
            },
            {
              label: strings.argumentListType.single,
              testID: 'list-type-arguments',
              style: { backgroundColor: 'red' },
              onPress: () => {
                setActiveListType("arguments");
              },
            },
          ]}
          onDismiss={() => setShowDropdownOptions(false)}
        />
    </>
  );
}